/**
 * Puente CONSUMO local-first: corrige una lección con `learning-blocks`, deriva
 * los eventos de gamificación y los aplica con `gamification-core`. Feedback
 * inmediato en el cliente; el servidor RE-CORRIGE y es la autoridad (grading.rs).
 */

import { dayKeyUTC } from "../gamification/streaks";
import { applyEvents } from "../gamification/engine";
import type { Awards, GameEvent, PlayerState } from "../gamification/types";
import {
  type AntifraudResult,
  type AttemptSubmission,
  verifyAttempt,
} from "../learning-blocks/antifraud";
import type { BlockRegistry } from "../learning-blocks/registry";
import type { AuthoredBlock, BlockResponse, LessonResult } from "../learning-blocks/types";
import { DEFAULT_LESSON_XP, SPACE_EVENTS, spaceGamificationConfig } from "./gamification";

/** Umbral de aprobado de una lección (coincide con el servidor: 0.6). */
export const LESSON_PASS_THRESHOLD = 0.6;

/** Resultado de consumir una lección (para UI + persistencia + gamificación). */
export interface LessonOutcome {
  lessonId: string;
  courseId: string;
  /** Nota 0..1 recalculada localmente (el servidor la re-verifica). */
  score: number;
  passed: boolean;
  perfect: boolean;
  /** XP configurado de la lección (autoritativo del contenido). */
  xpReward: number;
  /** ¿Completar esta lección completó el curso? */
  courseCompleted: boolean;
  /** Corrección detallada por bloque (para revisión). */
  result: LessonResult;
  /** Señales de antifraude locales (informativas; la autoridad es el server). */
  fraudFlags: AntifraudResult["flags"];
}

/** Construye la submission para `verifyAttempt` a partir de las respuestas. */
export function buildAttemptSubmission(
  lessonId: string,
  responses: BlockResponse[],
  opts: { claimedScore?: number; startedAt?: number; finishedAt?: number } = {},
): AttemptSubmission {
  return {
    lessonId,
    responses,
    claimedScore: opts.claimedScore,
    startedAt: opts.startedAt,
    finishedAt: opts.finishedAt,
  };
}

/** Corrige una lección localmente (misma lógica que re-verifica el servidor). */
export function gradeLessonAttempt(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  submission: AttemptSubmission,
): AntifraudResult {
  return verifyAttempt(registry, blocks, submission);
}

/** Deriva los eventos de gamificación de una lección completada. */
export function lessonCompletionEvents(
  outcome: LessonOutcome,
  at: number = Date.now(),
): GameEvent[] {
  if (!outcome.passed) {
    return [];
  }
  const day = dayKeyUTC(at);
  const events: GameEvent[] = [
    {
      type: SPACE_EVENTS.lessonCompleted,
      at,
      day,
      value: outcome.xpReward || DEFAULT_LESSON_XP,
      meta: { lessonId: outcome.lessonId, courseId: outcome.courseId },
    },
  ];
  if (outcome.perfect) {
    events.push({
      type: SPACE_EVENTS.lessonPerfect,
      at,
      day,
      meta: { lessonId: outcome.lessonId },
    });
  }
  if (outcome.courseCompleted) {
    events.push({
      type: SPACE_EVENTS.courseCompleted,
      at,
      day,
      meta: { courseId: outcome.courseId },
    });
  }
  return events;
}

/**
 * "Gamificación cableada": aplica el resultado de una lección al estado del
 * jugador con la config de Space. Devuelve el estado nuevo y las recompensas
 * (XP, badges, racha, subida de nivel) para que la UI las celebre.
 */
export function applyLessonCompletion(
  state: PlayerState,
  outcome: LessonOutcome,
  at: number = Date.now(),
): { state: PlayerState; awards: Awards } {
  const events = lessonCompletionEvents(outcome, at);
  return applyEvents(state, events, spaceGamificationConfig);
}

/**
 * Construye un `LessonOutcome` a partir de la corrección de una lección y el
 * XP configurado. `courseProgress` permite señalar si el curso queda completo.
 */
export function toLessonOutcome(params: {
  lessonId: string;
  courseId: string;
  xpReward: number;
  attempt: AntifraudResult;
  courseCompleted?: boolean;
}): LessonOutcome {
  const { result, flags } = params.attempt;
  const passed = result.score >= LESSON_PASS_THRESHOLD;
  return {
    lessonId: params.lessonId,
    courseId: params.courseId,
    score: result.score,
    passed,
    perfect: result.score >= 1,
    xpReward: params.xpReward,
    courseCompleted: params.courseCompleted ?? false,
    result,
    fraudFlags: flags,
  };
}
